import { CollectionConstant } from "presentation/constant/Collection/CollectionConstant";
import { collectionSubTypeDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHCheckbox, HPHInputTextarea, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CollectionSearchPanel = () => {
    const SCREEN_CONSTANT = CollectionConstant;
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { searchCriteria } = collectionState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const resetButtonClicked = useCallback(() => {
        collectionVM.onSearchCriteriaResetClick(operatingUnit);
    }, [collectionVM, operatingUnit]);

    const searchButtonClicked = useCallback(() => {
        setIsLoading(true);
        collectionVM.setIsSearchLoading(true);
        collectionVM.onSearch(searchCriteria).then((data) => {
            setIsLoading(false);
            collectionVM.setIsSearchLoading(false);
            collectionVM.resetDefaultSelectedRows();
        }).catch(error => {
            setIsLoading(false);
            collectionVM.setIsSearchLoading(false);
        })
    }, [collectionVM, searchCriteria])



    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria.billToCompany ?? ""}
                fieldValue={searchCriteria?.billToCompany ?? ""}
                fieldLabel={SCREEN_CONSTANT.BILLTO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={collectionState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.BILLTO_COMPANY, collectionState.dynamicOptions.companyCodeDropdownOptions, collectionVM, searchCriteria.billToCompany])


    const memoUpdatedBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria.updatedBy ?? ""}
                fieldValue={searchCriteria?.updatedBy ?? ""}
                fieldLabel={SCREEN_CONSTANT.UPDATE_BY}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'updatedBy'}
                maxLength={60}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.UPDATE_BY, collectionVM, searchCriteria.updatedBy])

    const memoSubType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria.subType ?? ""}
                fieldValue={searchCriteria?.subType ?? ""}
                fieldLabel={SCREEN_CONSTANT.SUB_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subType'}
                maxLength={60}
                options={collectionSubTypeDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.SUB_TYPE, collectionVM, searchCriteria.subType])

    const memoDateFromTo = useMemo(() =>
        <div className='im-flex-row-item'>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "dateFrom", endField: "dateTo" }}
                dateRange={{ startDate: searchCriteria.dateFrom, endDate: searchCriteria.dateTo }}
                onDatesChange={collectionVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.DATE_RANGE, collectionVM.onDateRangeChange, searchCriteria.dateFrom, searchCriteria.dateTo])

    const memoCollectionDateFromTo = useMemo(() =>
        <div className='im-flex-row-item'>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.COLLECTION_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "collectionDateFrom", endField: "collectionDateTo" }}
                dateRange={{ startDate: searchCriteria.collectionDateFrom, endDate: searchCriteria.collectionDateTo }}
                onDatesChange={collectionVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.COLLECTION_DATE_RANGE, collectionVM.onDateRangeChange, searchCriteria.collectionDateFrom, searchCriteria.collectionDateTo])


    const memoNotYetFinal = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                fieldKey={"activeInd"}
                requiredFieldList={[]}
                fieldLabel={SCREEN_CONSTANT.NOT_YET_FINAL}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={searchCriteria?.notYetFinal === 'Y'}
                disabled={false}
                onChange={(e) => collectionVM.onCheckboxChange(e.checked, "notYetFinal")}
            />
        </div>
        , [SCREEN_CONSTANT.NOT_YET_FINAL, searchCriteria?.notYetFinal, collectionVM])


    const memoDraftInvoiceNoAQCT = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria.draftInvoiceNo ?? ""}
                fieldValue={searchCriteria?.draftInvoiceNo ?? ""}
                fieldLabel={SCREEN_CONSTANT.DRAFT_INVOICE_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'draftInvoiceNo'}
                maxLength={12}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    collectionVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.DRAFT_INVOICE_NO, collectionVM, searchCriteria.draftInvoiceNo])

    const memoDraftInvoiceNo = useMemo(() =>
        <div className='flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <HPHInputTextarea
                label={SCREEN_CONSTANT.DRAFT_INVOICE_NO}
                width="470px"
                rows={3}
                resizable={false}
                helpIcon={true}
                toolTipText={"Use comma ( , ) as delimiter"}
                value={searchCriteria.draftInvoiceNoList?.join(",")}
                onChange={(e) => { collectionVM.onTextAreaChange(e, "draftInvoiceNoList", true) }} />
        </div>
        , [SCREEN_CONSTANT.DRAFT_INVOICE_NO, collectionVM, searchCriteria.draftInvoiceNoList])

    const memoReceiptNo = useMemo(() =>
        <div className='flex-row-item' style={{ width: "470px", marginBottom: "24px" }}>
            <HPHInputTextarea
                label={SCREEN_CONSTANT.RECEIPT_NO}
                width="470px"
                rows={3}
                resizable={false}
                helpIcon={true}
                toolTipText={"Use comma ( , ) as delimiter"}
                value={searchCriteria.receiptNoList?.join(",")}
                onChange={(e) => { collectionVM.onTextAreaChange(e, "receiptNoList", true) }} />
        </div>
        , [SCREEN_CONSTANT.RECEIPT_NO, collectionVM, searchCriteria.receiptNoList])

    return (
        <div className='side-form-content-left-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>{SCREEN_CONSTANT.SEARCH_CRITERIA}</SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={collectionVM.onSearchClick} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            {isLoading && <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? <>
                            <CriteriaItemContainer>
                                {memoDateFromTo}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoCollectionDateFromTo}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoBillToCompany}
                                {memoUpdatedBy}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoSubType}
                                {memoNotYetFinal}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoDraftInvoiceNo}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoReceiptNo}
                            </CriteriaItemContainer>

                        </> : <>

                            <CriteriaItemContainer>
                                {memoCollectionDateFromTo}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoBillToCompany}
                                {memoDraftInvoiceNoAQCT}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoNotYetFinal}
                            </CriteriaItemContainer>

                        </>}

                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked()} />
            </SidebarActionBar>
        </div>
    );
}

export default CollectionSearchPanel;
